/**
 * Gibbs Med-Chem Intuition — structured rules from Nick Meanwell's short-course
 * lectures (Isosteres, Fluorine, Heteroaromatic Rings, Toxicophores, S-Interactions)
 * and their canonical J. Med. Chem. / Adv. Het. Chem. reviews.
 *
 * Every rule cites its source. Gibbs must never paraphrase a rule without the
 * citation — the whole point is to ground LLM advice in published medchem truth.
 *
 * @module gibbs/knowledge/medchemRules
 */

/* eslint-disable max-len */
const MEANWELL_2011_ISOSTERES = 'Meanwell, N.A. Synopsis of Some Recent Tactical Application of Bioisosteres in Drug Design. J. Med. Chem. 2011, 54, 2529–2591.';
const MEANWELL_2017_HETERO = 'Meanwell, N.A. A Synopsis of the Properties and Applications of Heteroaromatic Rings in Medicinal Chemistry. Adv. Heterocycl. Chem. 2017, 123, 245–361.';
const GILLIS_MEANWELL_2015_F = 'Gillis, E.P.; Eastman, K.J.; Hill, M.D.; Donnelly, D.J.; Meanwell, N.A. Applications of Fluorine in Medicinal Chemistry. J. Med. Chem. 2015, 58, 8315–8359.';
const MEANWELL_2023_SULFUR = 'Meanwell, N.A. Applications of Non-Covalent Sulfur Interactions in Drug Design. Blumberg Institute lecture, 20 June 2023. See also J. Med. Chem. 2015, 58, 4383–4438 (Beno et al.).';
const MEANWELL_2023_TOX = 'Meanwell, N.A. Designing Around Problematic Functionalities in Drug Discovery. Blumberg Institute lecture, 25 July 2023.';
const MEANWELL_2011_TOX_REVIEW = 'Stepan, A.F.; Walker, D.P.; Bauman, J.; Price, D.A.; Baillie, T.A.; Kalgutkar, A.S.; Aleo, M.D. Structural alert/reactive metabolite concept as applied in medicinal chemistry. Chem. Res. Toxicol. 2011, 24, 1345–1410 (cited extensively in Meanwell 2023 tox lecture).';
/* eslint-enable max-len */

// =============================================================================
// ISOSTERES — Meanwell 2011 J. Med. Chem. is the canonical reference.
// =============================================================================
export const ISOSTERE_RULES = [
    {
        id: 'iso_cooh_tetrazole',
        category: 'isostere',
        from: 'carboxylic acid (-COOH)',
        to: '1H-tetrazole',
        principle:
            'Tetrazole is the classical non-classical bioisostere of -COOH: similar pKa (tetrazole ~4.9 vs COOH ~4.2), similar anionic footprint, ~2× greater lipophilicity, and improved metabolic stability (no β-oxidation, no glucuronidation of the acid).',
        applicability:
            'Use when the acid is essential for binding but causes PK liabilities (clearance via phase-II conjugation, poor permeability due to charge).',
        example:
            'Losartan (AT1 antagonist): tetrazole replaces COOH for oral exposure. Valsartan, candesartan follow the same pattern.',
        caveats:
            'Higher MW (~+26 Da), planar vs. freely rotating acid, possible PAINS/reactive-metabolite risk with some substitution patterns.',
        references: [MEANWELL_2011_ISOSTERES],
        fromPattern: '[CX3](=O)[OX2H1]',
        toFragment: 'c1nnn[nH]1',
        // gibbs-self-hosted-substructure-replace Path A (2026-05-15):
        // explicit reaction SMARTS lets compoundTransform run this
        // client-side via @rdkit/rdkit's run_reactants. The [*:1] atom
        // map carries the COOH's attachment atom into the product.
        reactionSmarts: '[*:1][CX3](=O)[OX2H1]>>[*:1]c1nnn[nH]1',
    },
    {
        id: 'iso_cooh_acylsulfonamide',
        category: 'isostere',
        from: 'carboxylic acid (-COOH)',
        to: 'acyl sulfonamide (-C(O)NHSO2R)',
        principle:
            'Acyl sulfonamides match -COOH in pKa (~4.5-5.5) and H-bond character but add a tunable R group on the sulfonyl to modulate lipophilicity and selectivity.',
        applicability:
            'When tetrazole is too polar or MW-adding and a lipophilicity lever is needed.',
        example:
            'Asunaprevir and other HCV NS3 protease inhibitors use acyl sulfonamide for potent P1′ interaction.',
        caveats: 'Plasma stability of N-H; solubility drop with large R groups.',
        references: [MEANWELL_2011_ISOSTERES],
        fromPattern: '[CX3](=O)[OX2H1]',
        toFragment: 'C(=O)NS(=O)(=O)C',
        reactionSmarts: '[*:1][CX3](=O)[OX2H1]>>[*:1]C(=O)NS(=O)(=O)C',
    },
    {
        id: 'iso_cooh_oxadiazolone',
        category: 'isostere',
        from: 'carboxylic acid (-COOH)',
        to: '1,2,4-oxadiazol-5(4H)-one (3-hydroxyoxadiazole)',
        principle:
            'Hydroxy-oxadiazoles retain the acidic proton and H-bond donor/acceptor pattern while offering improved cell permeability vs. the parent acid.',
        applicability: 'GPCR and nuclear-receptor ligands where COOH hurts CNS or oral exposure.',
        example: 'Pranlukast (CysLT1 antagonist) uses this motif.',
        caveats: 'Metabolic stability of the heterocycle must be validated.',
        references: [MEANWELL_2011_ISOSTERES],
        fromPattern: '[CX3](=O)[OX2H1]',
        toFragment: 'O=c1[nH]nco1',
        // gibbs-self-hosted-substructure-replace Path A v2 (2026-05-15).
        // Validated against benzoic acid → 3-phenyl-1,2,4-oxadiazol-5(4H)-one.
        reactionSmarts: '[*:1][CX3](=O)[OX2H1]>>[*:1]c1nco[nH]c1=O',
    },
    {
        id: 'iso_amide_oxadiazole',
        category: 'isostere',
        from: 'amide (-C(O)NR2)',
        to: '1,3,4-oxadiazole or 1,2,4-oxadiazole',
        principle:
            'Oxadiazoles mimic the amide planar H-bond geometry with the N-O-C fragment substituting for the C(O)-N. Loses the amide H-bond donor but preserves acceptor.',
        applicability:
            'When the amide is metabolically labile (protease cleavage, amidase) and the H-bond donor is not binding-essential.',
        example: 'Ataluren; tafamidis; several kinase inhibitors.',
        caveats:
            'Changes molecular geometry (~170° amide vs ring-fixed ~150°); metabolic stability varies by substitution pattern.',
        references: [MEANWELL_2011_ISOSTERES, MEANWELL_2017_HETERO],
        fromPattern: '[CX3](=O)[NX3]',
        toFragment: 'c1nnoc1',
        // Path A v2 (2026-05-15). Replaces the amide with a 1,2,4-
        // oxadiazole; the N-substituent is intentionally dropped (the
        // heterocycle replaces it, same as Chem.AllChem.ReplaceSubstructs
        // would produce). Validated against N-methylacetamide.
        reactionSmarts: '[*:1][CX3](=O)[NX3]>>[*:1]c1nnoc1',
    },
    {
        id: 'iso_amide_triazole',
        category: 'isostere',
        from: 'amide (-C(O)NHR)',
        to: '1,2,3-triazole (via click chemistry)',
        principle:
            'The 1,4-disubstituted triazole mimics amide geometry with an H-bond-accepting ring. Synthetically accessible via CuAAC click.',
        applicability:
            'Rapid SAR exploration, fragment linking, when amide cleavage is the primary liability.',
        example: 'Tazobactam β-lactamase inhibitor and many click-chemistry drug-discovery outputs.',
        caveats: 'Triazole is a known metal coordinator; check for off-target engagement.',
        references: [MEANWELL_2011_ISOSTERES, MEANWELL_2017_HETERO],
        fromPattern: '[CX3](=O)[NX3H]',
        toFragment: 'c1cn[nH]n1',
        // Path A v2 (2026-05-15). Amide → 1,2,3-triazole.
        // Validated against N-methylacetamide.
        reactionSmarts: '[*:1][CX3](=O)[NX3H]>>[*:1]c1cn[nH]n1',
    },
    {
        id: 'iso_phenyl_thiophene',
        category: 'isostere',
        from: 'phenyl',
        to: 'thiophene',
        principle:
            'Thiophene is a classical phenyl isostere with slightly smaller size, comparable logP, but σ-hole on S enables additional interactions (intra: 1,4-O→S; inter: σ-hole acceptor binding).',
        applicability:
            'Modest potency boost via added S interaction, or as a synthetic workaround for a phenyl SAR that has hit a wall.',
        example:
            'Clopidogrel, duloxetine, tiagabine — thiophene substitution of phenyl.',
        caveats:
            'Thiophene is a known toxicophore via S-oxidation to the electrophilic S-oxide. Mitigate by α-methyl blocking or fused bicycle.',
        references: [MEANWELL_2011_ISOSTERES, MEANWELL_2023_SULFUR, MEANWELL_2023_TOX],
        fromPattern: 'c1ccccc1',
        toFragment: 'c1ccsc1',
        // Path A v2 (2026-05-15). Phenyl → thiophene; deduplicated by
        // ring symmetry to a single product. Validated against toluene
        // → 2-methylthiophene (Cc1cccs1).
        reactionSmarts: '[*:1][c:2]1[cH:3][cH:4][cH:5][cH:6][cH:7]1>>[*:1][c:2]1[cH:3][cH:4][cH:5]s1',
    },
    {
        id: 'iso_phenyl_pyridine',
        category: 'isostere',
        from: 'phenyl',
        to: 'pyridine',
        principle:
            'Pyridine lowers logP by ~0.5-0.9 units, adds an H-bond acceptor, increases water solubility and basicity (pKa ~5.2), often reduces CYP affinity.',
        applicability:
            'When oral exposure is limited by low aqueous solubility or high CYP3A4 clearance.',
        example:
            'Many kinase inhibitors (imatinib, sunitinib) use pyridine in the hinge-binder region.',
        caveats:
            '2-Aminopyridine pattern may be an hERG risk via basic-N binding; 3-substituted pyridines generally lower risk.',
        references: [MEANWELL_2017_HETERO, MEANWELL_2011_ISOSTERES],
        fromPattern: 'c1ccccc1',
        toFragment: 'c1ccncc1',
        // Path A v2 (2026-05-15). Phenyl → pyridine (4-substituted
        // by default — matches the existing toFragment). Validated
        // against toluene → 4-methylpyridine (Cc1ccncc1). Other
        // isomers (3-pyridine, 2-pyridine) are valid analogs too;
        // SME may want a follow-up that enumerates all three.
        reactionSmarts: '[*:1][c:2]1[cH:3][cH:4][cH:5][cH:6][cH:7]1>>[*:1][c:2]1[c:3][c:4]n[c:6][c:7]1',
    },
    {
        id: 'iso_pyridine_diazine',
        category: 'isostere',
        from: 'pyridine',
        to: 'pyrimidine / pyridazine / pyrazine',
        principle:
            'Diazine isosteres each shift properties: pyrimidine lowers logP further and adds H-bond acceptor; pyridazine has strong dipole (~4 D) useful for buried polar interactions; pyrazine is most lipophilic of the three.',
        applicability: 'Fine-tuning polarity, dipole, and H-bond count after a pyridine SAR has plateaued.',
        example:
            'Minoxidil (pyrimidine), hydralazine (pyridazine), several antimalarials.',
        caveats: 'Metabolic soft spots shift; re-run metabolite ID.',
        references: [MEANWELL_2017_HETERO],
        // v1 picks pyrazine as the canonical replacement; pyrimidine and
        // pyridazine alternatives tracked as a followup in the study.
        fromPattern: 'c1ccncc1',
        toFragment: 'c1cnccn1',
    },
    {
        id: 'iso_gem_dimethyl_cyclopropyl',
        category: 'isostere',
        from: 'gem-dimethyl',
        to: 'cyclopropyl or bicyclo[1.1.1]pentyl (BCP)',
        principle:
            'Spirocycles and BCP act as sp3-enriched replacements for gem-dimethyl/phenyl, improving solubility, reducing hERG, and escaping flat-aromatic bias (rule of three-dimensionality).',
        applicability:
            'When the compound is too flat, too lipophilic, or has hERG liability.',
        example: 'BCP has been used to replace para-phenyl in antibacterials, GPCR ligands.',
        caveats:
            'Synthesis cost; ring strain can shift pKa of adjacent groups; logP change is modest for cyclopropyl.',
        references: [MEANWELL_2011_ISOSTERES],
    },
    {
        id: 'iso_phenol_hydroxypyridine',
        category: 'isostere',
        from: 'phenol (Ar-OH)',
        to: '2- or 3-hydroxy pyridine (or N-hydroxyamide tautomer)',
        principle:
            'Hydroxypyridines retain the H-bond donor of phenol but add a ring nitrogen, lowering logP and shifting pKa. Tautomerism can present an amide-like face.',
        applicability: 'When phenol causes glucuronidation-driven clearance.',
        example: 'Several HIV integrase and HCV polymerase inhibitors.',
        caveats: 'Tautomer ratio depends on substitution; validate binding mode.',
        references: [MEANWELL_2011_ISOSTERES, MEANWELL_2017_HETERO],
        fromPattern: 'Oc1ccccc1',
        toFragment: 'Oc1cccnc1',
        // Path A v2 (2026-05-15). Phenol → 4-hydroxypyridine
        // (canonical phenol-isostere pyridinol). Validated against
        // phenol → Oc1ccncc1.
        reactionSmarts: '[OH:8][c:1]1[cH:2][cH:3][cH:4][cH:5][cH:6]1>>[O:8][c:1]1[c:2][c:3][n:4][c:5][c:6]1',
    },
    {
        id: 'iso_ester_amide',
        category: 'isostere',
        from: 'ester (-C(O)OR)',
        to: 'amide (-C(O)NR2)',
        principle:
            'Amides resist esterase cleavage and extend t½. Loses one H-bond acceptor relative to ester, gains an H-bond donor if NH.',
        applicability:
            'Nearly universal when ester is a prodrug or metabolic liability and SAR tolerates.',
        example: 'Anti-inflammatory prostaglandin analogs; statins (atorvastatin vs. pravastatin).',
        caveats: 'Changes geometry from ~120° ester to flat amide; may lose key interaction.',
        references: [MEANWELL_2011_ISOSTERES],
        fromPattern: '[CX3](=O)[OX2][CX4]',
        toFragment: 'C(=O)N',
        // Path A v2 (2026-05-15). Ester → amide; the alkyl
        // substituent on O migrates to N. Validated against methyl
        // acetate → N-methyl acetamide (CNC(C)=O).
        reactionSmarts: '[*:1][CX3](=O)[OX2][CX4:2]>>[*:1]C(=O)N[*:2]',
    },
    {
        id: 'iso_amide_retroamide',
        category: 'isostere',
        from: 'amide (-C(O)NHR)',
        to: 'retro-amide (-NHC(O)R)',
        principle:
            'Reversing the amide bond preserves H-bond donor/acceptor pattern at similar geometry. Can unblock protease-recognition issues.',
        applicability: 'When plasma amidase or a specific protease is the metabolic liability.',
        example: 'Several NS3/4A and NS5A inhibitors during HCV optimization.',
        caveats: 'Secondary SAR shifts because the side chain is now on the N.',
        references: [MEANWELL_2011_ISOSTERES],
    },
    {
        id: 'iso_ch2_o_bioisostere',
        category: 'isostere',
        from: 'methylene (-CH2-)',
        to: 'ether oxygen (-O-)',
        principle:
            'Classical isostere: slightly shorter C–O bond (1.42 Å vs C–C 1.53 Å), adds an H-bond acceptor, lowers logP by ~0.3-0.5.',
        applicability: 'Lowering lipophilicity in alkyl chains or linkers.',
        example: 'Morpholine vs piperidine; methoxyphenyl vs ethylphenyl.',
        caveats: 'α-ether C–H becomes a CYP soft spot (oxidative de-alkylation).',
        references: [MEANWELL_2011_ISOSTERES],
    },
    // ─── Added 2026-05-14 (Theme D from Bill Kinney walkthrough) ───
    {
        id: 'iso_carboxylate_phosphonate',
        category: 'isostere',
        from: 'carboxylic acid (-COOH)',
        to: 'phosphonic acid (-P(O)(OH)2)',
        principle:
            'Phosphonic acid mimics the carboxylate anion with two ionizable OH groups (pKa1 ~2, pKa2 ~7), giving a -2 charge at physiological pH vs -1 for the carboxylate. Adds an H-bond donor, geometry is tetrahedral around P rather than trigonal-planar around C.',
        applicability:
            'When carboxylate-cation interactions (Lys / Arg salt bridges, metal coordination) need to be reinforced. Common in glutamate-binding pockets and metalloenzyme active sites.',
        example:
            'NMDA antagonists AP5 / AP7 / D-AP7 use phosphonate as the distal-carboxylate replacement of glutamate. Bisphosphonates (alendronate, zoledronate) for bone resorption.',
        caveats:
            'High polarity hurts oral exposure; phosphonate prodrugs (esters cleaved in vivo) are typically required for systemic delivery. Stronger metal chelation can be on- or off-target.',
        references: [MEANWELL_2011_ISOSTERES],
        // Matches BOTH protonated (-COOH) and deprotonated (-COO-) forms,
        // per SME (Jess) 2026-05-14. The existing COOH→tetrazole rule only
        // matches the protonated form; this rule needs to cover both since
        // glutamate-binding pockets typically see the deprotonated form.
        fromPattern: '[CX3](=O)[OX2H1,O-]',
        toFragment: 'P(=O)([O-])[O-]',
        // Path A v2 (2026-05-15). Carboxylate → phosphonate dianion.
        // Validated against benzoate → phenylphosphonate
        // (O=P([O-])([O-])c1ccccc1).
        reactionSmarts: '[*:1][CX3](=O)[OX2H1,O-]>>[*:1]P(=O)([O-])[O-]',
    },
    {
        id: 'iso_amino_acid_urea',
        category: 'isostere',
        from: 'α-amino acid (H2N-CR-COOH or HN-CR-COOH)',
        to: 'urea (-NH-C(O)-NH-)',
        principle:
            'Urea retains the bidentate H-bond donor/acceptor pattern of the amino-acid backbone (two N-H donors flanking a C=O acceptor) while collapsing the zwitterionic charge pair, planarizing the geometry, and eliminating one chiral center.',
        applicability:
            'When the amino-acid scaffold introduces zwitterion-driven PK liabilities or when the chiral α-center is a synthetic burden. Common in GPCR ligand series, especially histamine receptor antagonists.',
        example:
            'Histamine receptor (H1/H2/H3/H4) antagonists. Cimetidine uses a related guanidine motif; modern H3/H4 antagonist series often replace the amino-acid linker with urea or thiourea.',
        caveats:
            'Urea can be hydrolytically labile in some scaffolds and a metabolic liability; check stability. Loses the salt-bridge anchor that the carboxylate provided — may need rebalancing elsewhere.',
        references: [MEANWELL_2011_ISOSTERES],
        // Matches both primary (H2N-CR-COOH) and secondary (HN-CR-COOH)
        // α-amino acid backbones, per SME (Jess) 2026-05-14 — broader scope
        // pending Bill's confirmation on the future demo.
        fromPattern: '[NX3;H1,H2][CX4][CX3](=O)[OX2H1]',
        toFragment: 'NC(=O)N',
    },
];

// =============================================================================
// FLUORINE — Meanwell 2020 lecture + Gillis/Meanwell 2015 review.
// =============================================================================
export const FLUORINE_RULES = [
    {
        id: 'f_block_cyp_metabolism',
        category: 'fluorine',
        principle:
            'Placing F at a known CYP450 oxidation site blocks metabolism because the strong C–F bond resists abstraction and F is not a good leaving group. Most effective on benzylic positions, α-carbons of amines, and electron-rich aromatic positions.',
        applicability:
            'Compound shows rapid HLM turnover with a single dominant metabolite; place F at the identified metabolic soft spot.',
        example:
            'Ezetimibe → fluorinated analog; many CYP3A4-labile drugs stabilized by para-F on metabolized aryl.',
        caveats:
            'Metabolism may shift to a secondary site, not disappear. Aromatic F can induce para-directed metabolism nearby.',
        references: [GILLIS_MEANWELL_2015_F],
    },
    {
        id: 'f_pka_modulation_amine',
        category: 'fluorine',
        principle:
            'β-Fluorine lowers amine pKa by ~1.7 units per F; γ-F drops it by ~0.5 unit. CF3 group lowers pKa of a nearby amine by ~2.0.',
        applicability:
            'Moderating basicity of an amine to reduce P-gp efflux, hERG, or phospholipidosis.',
        example:
            'β-Fluoroamines in CNS drugs; CF3 on piperidines to cut hERG.',
        caveats:
            'Too much pKa depression can kill the basic-amine binding interaction; compute target pKa change first.',
        references: [GILLIS_MEANWELL_2015_F],
    },
    {
        id: 'f_gauche_effect',
        category: 'fluorine',
        principle:
            'The C–F bond prefers a gauche orientation relative to other polar σ bonds (N+–H, O–H, C–F): 1,2-difluoroalkanes and β-fluoroamines adopt gauche conformations energetically favored by ~2-4 kJ/mol.',
        applicability:
            'Conformational preorganization of flexible linkers; bioactive-conformation locking without ring formation.',
        example:
            'Odanacatib, several GPCR ligands use β-F amine gauche effect.',
        caveats: 'Effect is small (2-4 kJ/mol); rarely sufficient alone to enforce a single conformer.',
        references: [GILLIS_MEANWELL_2015_F],
    },
    {
        id: 'f_multipolar_interaction',
        category: 'fluorine',
        principle:
            'C–F can engage in multipolar interactions with C=O (amide dipoles): orthogonal F⋯C=O at ~3.5 Å contributes ~1 kcal/mol per contact.',
        applicability:
            'Hidden potency boost when a protein amide/carbonyl lies near an aryl ring: put an ortho-F there.',
        example: 'Thrombin, elastase, and many kinase inhibitor SAR series.',
        caveats: 'Requires X-ray or MD to place correctly; magnitude is modest.',
        references: [GILLIS_MEANWELL_2015_F],
    },
    {
        id: 'f_as_h_bond_acceptor',
        category: 'fluorine',
        principle:
            'C–F is a WEAK H-bond acceptor (~0.2-0.5 kcal/mol). Often overestimated in docking scores. The lone pair geometry is poor (sp3-like lone pairs with short C–F bond).',
        applicability:
            'Rarely reliable as a binding driver. Use only when rigorously validated by structural data.',
        example:
            'Claims of C–F⋯H–N H-bonds in kinase hinge regions are usually multipolar, not H-bond.',
        caveats:
            'Do NOT optimize for C–F H-bonds without X-ray confirmation.',
        references: [GILLIS_MEANWELL_2015_F],
    },
    {
        id: 'f_cf3_size_lipophilicity',
        category: 'fluorine',
        principle:
            'CF3 is roughly isosteric with isopropyl in size (~38 vs 56 Å3 Van der Waals), but much more lipophilic (π-CF3 ~+1.0) and metabolically inert. Not equivalent to a methyl in size.',
        applicability:
            'Replacing a small alkyl to tune lipophilicity + metabolic stability without major steric change.',
        example: 'Celecoxib (CF3 on pyrazole); sitagliptin (CF3 triazole).',
        caveats:
            'CF3 adds significant MW and reduces aqueous solubility; check sol first.',
        references: [GILLIS_MEANWELL_2015_F],
    },
    {
        id: 'f_membrane_permeability',
        category: 'fluorine',
        principle:
            'Strategic F placement reduces intramolecular H-bond donor exposure and can raise membrane permeability. Ring F masking a nearby NH raises logD by ~0.2-0.4.',
        applicability: 'Low-permeability compounds with accessible NH or OH.',
        example: 'Several HCV NS5A inhibitors were optimized this way.',
        caveats: 'Too much F raises logP past the solubility cliff.',
        references: [GILLIS_MEANWELL_2015_F],
    },
    {
        id: 'f_sf5_emerging',
        category: 'fluorine',
        principle:
            'SF5 (pentafluorosulfanyl) is "super CF3": more lipophilic (π ~+1.5), larger (~85 Å3), and extraordinarily stable. Used as a late-stage tunability lever.',
        applicability: 'When CF3 is not enough lipophilicity or when steric bulk is desired.',
        example: 'Emerging in anti-infectives and PET tracer design.',
        caveats: 'Synthesis cost; limited commercial building blocks.',
        references: [GILLIS_MEANWELL_2015_F],
    },
];

// =============================================================================
// HETEROAROMATIC RINGS — Meanwell 2017 Adv. Het. Chem.
// =============================================================================
export const HETEROAROMATIC_RULES = [
    {
        id: 'het_pyridine_vs_phenyl_properties',
        category: 'heteroaromatic',
        ring: 'pyridine',
        principle:
            'Pyridine vs. phenyl: logP −0.65, adds 1 H-bond acceptor, pKaH ~5.2 (basic N), stronger dipole (2.2 D). Reduces plasma protein binding and often improves oral exposure.',
        applicability:
            'Flat aromatic SAR that has reached a lipophilicity/solubility ceiling.',
        example: 'Many kinase hinge binders; celecoxib analogs.',
        caveats: 'Basicity increases hERG risk; 3-subst. pyridine typically safer than 2-amino.',
        references: [MEANWELL_2017_HETERO],
    },
    {
        id: 'het_imidazole_triazole_preferences',
        category: 'heteroaromatic',
        ring: 'imidazole/triazole',
        principle:
            'Imidazole (pKaH ~7.0) is basic and a strong H-bond donor; 1,2,3-triazole (pKaH ~1.2) is non-basic with a defined H-bond acceptor pattern. Both are flat and small (~similar size to phenyl).',
        applicability:
            'Need a basic-N like histidine-mimicking interaction → imidazole. Need amide-geometry mimic without basicity → triazole.',
        example: 'Cimetidine (imidazole); rufinamide (triazole).',
        caveats:
            'Imidazole is a potent CYP inhibitor (Fe coordination) — a known off-target liability.',
        references: [MEANWELL_2017_HETERO],
    },
    {
        id: 'het_thiazole_oxazole_isosteres',
        category: 'heteroaromatic',
        ring: 'thiazole/oxazole',
        principle:
            '1,3-Thiazole vs. 1,3-oxazole: thiazole is more lipophilic (+0.5 logP), larger (S larger than O), and contributes σ-hole interaction potential. Oxazole is more polar, smaller.',
        applicability: 'Fine-tuning logP and specific H-bond geometry.',
        example: 'Dasatinib (thiazole hinge); many β-lactam antibiotics (oxazolidinones).',
        caveats:
            '2-Aminothiazole can be a toxicophore (bioactivation to reactive imine); avoid unsubstituted 2-amino.',
        references: [MEANWELL_2017_HETERO, MEANWELL_2023_TOX],
    },
    {
        id: 'het_pyrazole_isoxazole_indazole',
        category: 'heteroaromatic',
        ring: 'pyrazole/isoxazole/indazole',
        principle:
            'Pyrazole (pKaH ~2.5): weakly basic, planar, two H-bond acceptor N atoms, often the hinge binder in kinase inhibitors. Isoxazole is more electron-poor; indazole is benzo-fused pyrazole with stronger stacking.',
        applicability:
            'Kinase hinge, GPCR agonist, any hinge/pocket where planar aromatic + H-bond acceptor needed.',
        example: 'Crizotinib (pyrazole); sildenafil pyrazolopyrimidine.',
        caveats:
            'Isoxazole can be bioactivated to a reactive aziridine/oxirane → check metabolite ID.',
        references: [MEANWELL_2017_HETERO],
    },
    {
        id: 'het_pyrimidine_purine_hinge',
        category: 'heteroaromatic',
        ring: 'pyrimidine/purine',
        principle:
            'Pyrimidines and purines provide 2-3 H-bond interactions with kinase hinge backbone. 7-Azaindole and 7-deazapurines offer sterically constrained alternatives.',
        applicability: 'Almost all Type-I kinase inhibitors; ATP-competitive binding.',
        example: 'Imatinib, dasatinib, gefitinib all pyrimidine; zanubrutinib purine.',
        caveats: 'Promiscuity is the default — selectivity must be engineered elsewhere.',
        references: [MEANWELL_2017_HETERO],
    },
    {
        id: 'het_chemical_stability_ranking',
        category: 'heteroaromatic',
        principle:
            'Metabolic liability ranking (approximate, most labile → most stable): furan > thiophene > pyrrole > thiazole ≈ oxazole > pyridine ≈ pyrimidine > benzene.',
        applicability:
            'When picking between similar isosteres under metabolic pressure, prefer the more stable ring.',
        example: 'General heuristic guides early hit-to-lead choices.',
        caveats:
            'Stability depends heavily on substitution — α-methyl blocks many oxidation sites; context matters.',
        references: [MEANWELL_2017_HETERO, MEANWELL_2023_TOX],
    },
];

// =============================================================================
// TOXICOPHORES — Meanwell 2023 lecture + Stepan/Kalgutkar 2011 review.
// =============================================================================
export const TOXICOPHORE_RULES = [
    {
        id: 'tox_aniline_dili',
        category: 'toxicophore',
        alert: 'aniline (ArNH2) / aryl amine',
        smarts: '[NX3;H2][c]',
        mechanism:
            'CYP-mediated N-hydroxylation → nitroso → electrophilic; DNA adduction; hepatotoxicity (DILI) and methemoglobinemia risk.',
        mitigation:
            'Block N-oxidation by (a) steric ortho substitution, (b) electron-withdrawing para (e.g., -CN, -CF3) to lower N electron density, (c) replace piperazine/piperidine N with C (as Meanwell notes for FMS kinase inhibitors), (d) substitute to form amide or sulfonamide.',
        // Structured mitigation options for substructure-aware rendering.
        // Surfaced 2026-05-14 by Bill Kinney: the prose mitigation listed
        // "replace piperazine/piperidine N with C" but the compound had
        // neither substructure, making the suggestion confusing. With
        // `mitigationOptions`, the rendering layer can filter context-
        // dependent suggestions against the compound's actual structure.
        // When this field is present, format-for-compound helpers use it
        // in preference to the prose `mitigation` field.
        mitigationOptions: [
            {
                strategy: 'ortho-block',
                description: 'Add a steric substituent ortho to the amine to block N-oxidation.',
                // No requiresSubstructure → universally applicable when the rule matches
            },
            {
                strategy: 'ewg-para',
                description: 'Add an electron-withdrawing group para (e.g., -CN, -CF3) to lower N electron density.',
            },
            {
                strategy: 'N-to-C-swap',
                description: 'Replace piperazine/piperidine N with C (as Meanwell notes for FMS kinase inhibitors).',
                // Only show this option when the compound actually contains
                // a piperazine or piperidine — otherwise it confuses the user.
                requiresSubstructure: ['N1CCNCC1', 'N1CCCCC1'],
            },
            {
                strategy: 'amide-or-sulfonamide',
                description: 'Convert the aniline N to an amide or sulfonamide.',
            },
        ],
        example:
            'Nimesulide, sulfonamide antibiotics — historical DILI. Modern FMS inhibitors replaced aniline with pyridyl to eliminate GSH adduct.',
        references: [MEANWELL_2023_TOX, MEANWELL_2011_TOX_REVIEW],
        // v1 mitigation = ortho-block with methyl. Other recipes (CN/CF3
        // para-block, N→C swap, conversion to amide) are SME-review-pending.
        mitigationPattern: 'Nc1ccccc1',
        toFragment: 'Nc1ccccc1C',
        // gibbs-self-hosted-substructure-replace Path A (2026-05-15):
        // run client-side ortho-block. Each ortho position fires as a
        // separate match; runReaction de-duplicates to a single analog.
        reactionSmarts: '[NX3;H2:1][c:2]1[cH:3][cH:4][cH:5][cH:6][cH:7]1>>[N:1][c:2]1[c:3](C)[c:4][c:5][c:6][c:7]1',
    },
    {
        id: 'tox_furan_bioactivation',
        category: 'toxicophore',
        alert: 'furan',
        smarts: 'c1ccoc1',
        mechanism:
            'CYP-mediated epoxidation → reactive cis-enedial → protein adduct → hepatotoxicity and CYP inactivation (mechanism-based inhibitor, MBI).',
        mitigation:
            '(a) Replace furan with oxazole, thiazole, or pyrrole, (b) 2,5-disubstitute to block epoxidation, (c) use a benzo-fused analog.',
        example: 'L-739749 (Merck angiotensin): furan → thiazole; bergamottin (grapefruit juice) is the classic furan CYP3A4 MBI.',
        references: [MEANWELL_2023_TOX],
        // v1 mitigation = swap furan for oxazole.
        mitigationPattern: 'c1ccoc1',
        toFragment: 'c1cnco1',
    },
    {
        id: 'tox_thiophene_s_oxide',
        category: 'toxicophore',
        alert: 'thiophene (unblocked)',
        smarts: '[cH]1[cH][cH][cH]s1',
        mechanism:
            'S-oxidation to thiophene S-oxide (electrophile) → Michael acceptor → GSH adduct → DILI. Risk highest when α positions are unsubstituted.',
        mitigation:
            '(a) Replace with phenyl or thiazole, (b) block with electron-withdrawing group, (c) fuse to benzothiophene, (d) α-alkylate to prevent S-oxidation.',
        example:
            'Tienilic acid (withdrawn, DILI); clopidogrel — uses bioactivation productively (prodrug).',
        references: [MEANWELL_2023_TOX, MEANWELL_2023_SULFUR],
    },
    {
        id: 'tox_hydrazine_hepatotox',
        category: 'toxicophore',
        alert: 'hydrazine (-NH-NH2), hydrazide (-C(O)-NH-NH2)',
        smarts: '[NX3;!H0][NX3;H2]',
        mechanism:
            'Oxidation to reactive diazonium → hepatotoxicity (idiosyncratic). Isoniazid is the archetype.',
        mitigation: 'Avoid if possible. Replace with amine, amide, or alternative warhead.',
        example: 'Isoniazid-induced hepatitis; iproniazid withdrawn.',
        references: [MEANWELL_2023_TOX],
        // v1 mitigation = collapse N-N to a single amine.
        mitigationPattern: '[NX3;!H0][NX3;H2]',
        toFragment: 'N',
        // Path A v2 (2026-05-15). Hydrazine → primary amine
        // (collapses the N-N bond). Validated against ethylhydrazine
        // → ethylamine (CCN).
        reactionSmarts: '[*:1][NX3;!H0][NX3;H2]>>[*:1]N',
    },
    {
        id: 'tox_michael_acceptor',
        category: 'toxicophore',
        alert: 'α,β-unsaturated carbonyl / Michael acceptor',
        smarts: '[CX3]=[CX3][CX3]=[OX1]',
        mechanism:
            'Non-selective thiol adduct with GSH or protein Cys residues. Can be intentional (covalent drug) or unintentional (toxicity).',
        mitigation:
            'If unintentional: saturate (α-methyl β-unsub), cyclize (lactone/lactam), or replace with non-Michael carbonyl. If intentional: tune reactivity with β-substituent (aryl lowers, alkyl raises) and use structural targeting for selectivity.',
        example: 'Acrylamide warhead in covalent BTK and EGFR drugs (ibrutinib, osimertinib).',
        references: [MEANWELL_2023_TOX],
        // v1 mitigation = saturate the α,β alkene. Cyclization and
        // non-Michael alternatives are followup recipes.
        mitigationPattern: 'C=CC=O',
        toFragment: 'CCC=O',
        // Path A v2 (2026-05-15). α,β-unsaturated carbonyl → saturated
        // alkyl carbonyl (Michael acceptor neutralized). Validated
        // against acrolein → propanal.
        reactionSmarts: '[CH2:1]=[CH:2][CH:3]=[O:4]>>[CH3:1][CH2:2][CH:3]=[O:4]',
    },
    {
        id: 'tox_aminothiazole',
        category: 'toxicophore',
        alert: '2-aminothiazole',
        smarts: '[NX3;H2]c1nccs1',
        mechanism:
            'S-oxidation and iminothione formation → reactive intermediate → GSH depletion and DILI.',
        mitigation:
            'Replace 2-amino with 2-methyl or 2-alkyl; or substitute thiazole with thiadiazole or pyrazole.',
        example: 'Sudoxicam withdrawn; meloxicam (thiadiazole analog) safe.',
        references: [MEANWELL_2023_TOX, MEANWELL_2017_HETERO],
    },
    {
        id: 'tox_quinone_imine_quinone_methide',
        category: 'toxicophore',
        alert: 'para-substituted phenol / quinone-imine precursor',
        smarts: '[OX2;H1]c1ccc([NX3,OX2,CX4])cc1',
        mechanism:
            'Two-electron oxidation → quinone-imine or quinone-methide → electrophile toward Cys/Lys/GSH.',
        mitigation:
            'Block ortho positions with alkyl or halide; remove para-activating groups; consider hydroxypyridine isostere.',
        example: 'Acetaminophen → NAPQI (hepatotoxic metabolite); paracetamol overdose.',
        references: [MEANWELL_2023_TOX],
    },
    {
        id: 'tox_nitroaromatic',
        category: 'toxicophore',
        alert: 'nitroaromatic (Ar-NO2)',
        smarts: '[NX3](=[OX1])(=[OX1])[c]',
        mechanism:
            'Nitroreductase / CYP reduction → nitro radical anion, nitroso, hydroxylamine → genotoxicity, DILI.',
        mitigation:
            'Replace with CF3, CN, or halogen; use in anti-infectives (metronidazole) where selective reduction by pathogen is feature, not bug.',
        example: 'Nilutamide (tolerable in cancer); flutamide (DILI withdrawn indication).',
        references: [MEANWELL_2023_TOX],
    },
    {
        id: 'tox_thiazolidinedione',
        category: 'toxicophore',
        alert: 'thiazolidine-2,4-dione (TZD)',
        smarts: 'O=C1NC(=O)CS1',
        mechanism:
            'Ring opening / S-oxidation → reactive α,β-unsaturated thioester; associated with hepatotoxicity.',
        mitigation: 'Replace TZD with oxazolidinedione or carboxylic acid isostere.',
        example: 'Troglitazone (withdrawn, DILI); pioglitazone tolerated with monitoring.',
        references: [MEANWELL_2023_TOX],
    },
    {
        id: 'tox_structural_alert_screen',
        category: 'toxicophore',
        alert: 'general structural alert screening',
        principle:
            'Run every new scaffold through a structural-alert filter (e.g., Kalgutkar/Stepan 2011 list; Glaxo filter). Presence of an alert is a warning, not a verdict — context and dose matter.',
        mitigation:
            '(a) Assess metabolic stability (HLM) + GSH trap, (b) run covalent-binding assay (14C-labeled parent), (c) design out the alert before lead nomination.',
        references: [MEANWELL_2023_TOX, MEANWELL_2011_TOX_REVIEW],
    },
];

// =============================================================================
// SULFUR NON-COVALENT INTERACTIONS — Meanwell 2023 lecture.
// =============================================================================
export const SULFUR_INTERACTION_RULES = [
    {
        id: 's_sigma_hole_theory',
        category: 's-interaction',
        principle:
            'σ-Holes form at the back of C–S bonds (opposite to the bonding orbital), creating a region of positive electrostatic potential. Electron-deficient S atoms (thiophene, thiazole, sulfones, disulfides) have deeper σ-holes that can bind electron-rich atoms (O, N, halogen) — analogous to halogen bonds.',
        applicability:
            'Whenever a heteroaromatic S is placed near a protein carbonyl, nitrogen, or halogen, a σ-hole interaction may be contributing.',
        example: 'Riluzole, clopidogrel, many kinase inhibitors.',
        caveats: 'Interaction is weak (~60-70% of H-bond energy); not a primary binding driver.',
        references: [MEANWELL_2023_SULFUR],
    },
    {
        id: 's_14_OS_interaction',
        category: 's-interaction',
        principle:
            '1,4 O→S interaction: intramolecular interaction between a carbonyl O and a ring S two bonds apart. O–S distance ~2.9-3.1 Å, SCCO torsion ~0-10°. Electrostatic in nature. Locks the thiophene/thiazole ring in a preferred conformation.',
        applicability:
            'Conformational locking of a bioactive pose without explicit ring fusion; key in benzothiophene-based FXa inhibitors, tiazofurin.',
        example:
            'Tiazofurin (IMPDH inhibitor): 1,4 O→S holds the thiazole ring in the conformation recognized by the enzyme.',
        references: [MEANWELL_2023_SULFUR],
    },
    {
        id: 's_15_and_16_OS_NS',
        category: 's-interaction',
        principle:
            '1,5 and 1,6 O→S or N→S interactions are stronger than 1,4 and involve orbital overlap (σ*(C–S) ← lone pair donor). Produce near-linear geometries.',
        applicability:
            'Engineered into macrocyclic or fused scaffolds for conformational preorganization.',
        example:
            'N→S interactions in 2-thienylpyridines; 3-methylpyridine enhances 1,5 N→S by inductive donation.',
        references: [MEANWELL_2023_SULFUR],
    },
    {
        id: 's_isosteric_chlorine',
        category: 's-interaction',
        principle:
            'S≡Cl/Br isosterism: a sulfur σ-hole can substitute for a C-Cl or C-Br halogen-bond interaction at comparable lipophilicity.',
        applicability:
            'When halogen-bonding is contributing and a chlorine is a metabolic liability (dehalogenation), replace with a thiophene or thiazole.',
        example: 'Several FXa and thrombin inhibitors.',
        references: [MEANWELL_2023_SULFUR],
    },
    {
        id: 's_isosteric_nh',
        category: 's-interaction',
        principle:
            'S≡NH isosterism: an S atom can mimic an NH in H-bond geometry with lower desolvation penalty (more lipophilic).',
        applicability:
            'When an NH is giving up too much binding energy to desolvation; swap to a matched S.',
        example: 'Thiophene vs pyrrole as amide-mimicking heterocycle.',
        references: [MEANWELL_2023_SULFUR],
    },
];

// =============================================================================
// INTERACTION TUNING — how to strengthen specific protein-ligand interactions.
// Added 2026-05-14 from the Bill Kinney walkthrough Theme G. Bill's question
// when looking at a pi-pi contact: "how would I modify my molecule to make it
// better?" — and his observation that the electron-complementarity intuition
// is "not really intuitive to a chemist." This category fills that gap with
// rules-of-thumb for strengthening each major interaction class.
//
// **SME review pending** — initial rules sourced from the existing Meanwell
// pool by tool-builder triangulation. Jessica (chemist) signs off before
// the first commit lands; broader SME review (Sam, Bill) at the next
// walkthrough validates the chemistry quality.
// =============================================================================
export const INTERACTION_TUNING_RULES = [
    {
        id: 'tune_pipi_electron_complementarity',
        category: 'interaction-tuning',
        interaction: 'pi-pi stacking',
        principle:
            'Pi-pi stacking is strengthened by donor-acceptor electronic complementarity between the two aromatic rings — pair an electron-rich ring (alkyl, alkoxy, amino-substituted) with an electron-poor ring (fluoro, cyano, nitro, halogenated). Hunter-Sanders model: the dominant attractive term comes from the partial charges, not the dispersion.',
        applicability:
            'When a pi-pi contact is identified (e.g., to a phenylalanine, tyrosine, tryptophan, or histidine sidechain) and SAR suggests the contact is binding-relevant but suboptimal.',
        example:
            'Hydroxyphenol-fluorophenyl pairings in kinase inhibitor SAR. Imatinib uses a pyridine (electron-poor) stacking against a tyrosine sidechain (electron-rich).',
        caveats:
            'Stronger pi-pi can come at cost of solubility (logP up) and CYP affinity (planar electron-rich rings). Verify with energy scoring before committing to the SAR direction.',
        references: [MEANWELL_2011_ISOSTERES, MEANWELL_2017_HETERO],
    },
    {
        id: 'tune_hbond_donor_strength',
        category: 'interaction-tuning',
        interaction: 'hydrogen bond (donor side)',
        principle:
            'H-bond donor strength scales with the donor atom\'s pKa and the inductive effect of its substituents. Adding electron-withdrawing groups (-CN, -CF3, -NO2, halogens) at the para or ortho position of a phenolic O-H or aromatic N-H lowers the pKa, increasing donor strength (Abraham α scale).',
        applicability:
            'When a polar contact to a protein acceptor (carbonyl O, hydroxyl O, backbone N) is identified but the donor strength is on the weak side. Park-Avenue / Lipinski-friendly substituents preferred for early SAR.',
        example:
            'Phenol (pKa ~10, α ~0.6) → 4-cyanophenol (pKa ~7.8, α ~0.8): meaningful donor-strength boost. Sulfonamides (pKa ~10) often outperform amides (pKa ~17) as donors against backbone-carbonyl acceptors.',
        caveats:
            'EWG additions affect off-target activity (often increasing CYP affinity); validate retention of selectivity. Donors that are too acidic (pKa < 6) start losing protonation at physiological pH and the H-bond weakens.',
        references: [MEANWELL_2011_ISOSTERES, GILLIS_MEANWELL_2015_F],
    },
    {
        id: 'tune_hbond_acceptor_strength',
        category: 'interaction-tuning',
        interaction: 'hydrogen bond (acceptor side)',
        principle:
            'H-bond acceptor strength scales with the acceptor atom\'s lone-pair availability and electron density. Pyridine N basicity (pKaH ~5) is a stronger acceptor than amide carbonyl (~-1) due to lone-pair availability. Electron-donating groups (alkoxy, alkyl, amino) on a heteroaromatic N increase acceptor strength; electron-withdrawing groups (CN, CF3) decrease it.',
        applicability:
            'When a heteroaromatic N is engaging an H-bond donor on the protein side (Asn/Gln NH2, Tyr OH, backbone NH, water bridge). Tuning pyridine substitution patterns is the canonical lever.',
        example:
            'Pyridine vs pyrimidine vs pyrazine: pyridine has the most basic N (pKaH 5.2), pyrazine the least (0.4). Choosing 3-methylpyridine vs 3-cyanopyridine swings acceptor strength by ~3 pKa units.',
        caveats:
            'Increasing basicity beyond pKa ~7 starts to introduce protonation issues at physiological pH (cationic species). Acceptors that are too basic can disrupt the H-bond by being protonated.',
        references: [MEANWELL_2011_ISOSTERES, MEANWELL_2017_HETERO],
    },
    {
        id: 'tune_halogen_bond_sigma_hole',
        category: 'interaction-tuning',
        interaction: 'halogen bond',
        principle:
            'Halogen bond strength scales with σ-hole depth: I > Br > Cl >> F. The σ-hole forms on the back of the C–X bond (opposite to the C). Electron-withdrawing groups on the same carbon deepen the σ-hole — perfluoroaryl-Cl is a stronger halogen-bond donor than a phenyl-Cl. F has essentially no σ-hole and does not halogen-bond (its productive interactions are multipolar / dipolar, not σ-hole).',
        applicability:
            'When SAR shows a halogen contact to a protein electron-rich site (carbonyl O, amide N, Tyr/Ser OH). Br/I work for strong σ-hole contributions; Cl is a balanced choice if metabolic stability matters; F is for multipolar / pKa tuning rather than σ-hole.',
        example:
            'PDE10 inhibitors with para-Cl on the aryl ring engage a Tyr carbonyl via σ-hole. Switching to Br often boosts potency at cost of MW + logP. Switching to F kills the σ-hole interaction entirely.',
        caveats:
            'Heavier halogens (Br, I) add MW (~+79, +127 Da each) and logP. I has thyroid-side-effect risk in some scaffolds. CF3 is NOT a halogen-bond donor — it\'s a polar-multipolar contributor.',
        references: [GILLIS_MEANWELL_2015_F, MEANWELL_2023_SULFUR],
    },
    {
        id: 'tune_salt_bridge_charge',
        category: 'interaction-tuning',
        interaction: 'salt bridge (electrostatic)',
        principle:
            'Salt bridge strength depends on (a) the charge magnitude of each partner and (b) the dielectric / solvation environment of the contact. Replacing a carboxylate (-1 at physiological pH) with a phosphonate (-2) doubles the formal charge, deepening the salt bridge to a basic residue (Lys, Arg). Tetrazole is isoelectronic with carboxylate but shifts pKa slightly.',
        applicability:
            'When a carboxylate-cation contact (e.g., Lys/Arg salt bridge) is identified and SAR suggests the contact is potency-limiting. Glutamate-binding pockets (NMDA, AMPA receptors) are the canonical case.',
        example:
            'NMDA antagonist AP5 uses two phosphonates on either end of the linker, mimicking glutamate but with stronger salt-bridge engagement to the binding pocket\'s Lys-273.',
        caveats:
            'Higher formal charge hurts membrane permeability significantly — phosphonates typically require prodrug strategies for oral exposure. Sulfonate (-1) is sometimes a better permeability/strength balance than phosphonate (-2).',
        references: [MEANWELL_2011_ISOSTERES],
    },
    {
        id: 'tune_cation_pi_aromatic_density',
        category: 'interaction-tuning',
        interaction: 'cation-π',
        principle:
            'Cation-π interactions are strengthened by increasing the aromatic ring\'s electron density (electron-donating substituents) and weakened by electron-withdrawing groups. The interaction is dominated by the cation\'s electrostatic attraction to the aromatic π-cloud; richer π density = deeper attractive potential. Dougherty pioneered the quantitative framework.',
        applicability:
            'When a quaternary ammonium, Lys/Arg sidechain, or other cation is engaging an aromatic ring on the ligand. Tuning the ring\'s substituents shifts the interaction strength. Cation-π is often the dominant interaction in acetylcholine-binding pockets (nAChR, AChE).',
        example:
            'AChE inhibitor design: indole > benzene > nitrobenzene for cation-π engagement with the active-site Trp. Methylating a phenyl to xylyl meaningfully strengthens the contact.',
        caveats:
            'Maximizing aromatic electron density tends to increase logP and CYP affinity. Balance the gain against ADME impact.',
        references: [MEANWELL_2011_ISOSTERES, MEANWELL_2017_HETERO],
    },
];

// =============================================================================
// Unified registry — all rules in one list for retrieval.
// =============================================================================
export const ALL_MEDCHEM_RULES = [
    ...ISOSTERE_RULES,
    ...FLUORINE_RULES,
    ...HETEROAROMATIC_RULES,
    ...TOXICOPHORE_RULES,
    ...SULFUR_INTERACTION_RULES,
    ...INTERACTION_TUNING_RULES,
];

// =============================================================================
// Retrieval helpers.
// =============================================================================

/**
 * Get all rules in a category.
 * @param {'isostere'|'fluorine'|'heteroaromatic'|'toxicophore'|'s-interaction'} category
 * @returns {Array<object>}
 */
export function getRulesByCategory(category) {
    return ALL_MEDCHEM_RULES.filter((r) => r.category === category);
}

/**
 * Look up a single rule by its `id`. Returns `null` for unknown IDs so
 * UI surfaces can render an explicit "no citation" / "rule unknown"
 * fallback rather than crashing on a stale flag identifier.
 *
 * @param {string} ruleId
 * @returns {object|null}
 */
export function getRuleById(ruleId) {
    if (!ruleId || typeof ruleId !== 'string') return null;
    return ALL_MEDCHEM_RULES.find((r) => r.id === ruleId) || null;
}

/**
 * Keyword match against a user query. Returns rules with simple relevance score.
 * Intentionally NOT TF-IDF — rules are small; a simple token overlap is fine
 * and keeps the module dependency-free.
 *
 * @param {string} queryText
 * @param {number} [max=6]
 * @returns {Array<object>} matched rules, best first
 */
export function findRelevantRules(queryText, max = 6) {
    const q = (queryText || '').toLowerCase();
    if (!q) return [];

    const catKeywords = [
        { cat: 'isostere', words: ['isostere', 'bioisostere', 'replace', 'swap', 'substitute'] },
        { cat: 'fluorine', words: ['fluorine', 'fluorinate', 'cf3', 'c-f', 'add f'] },
        { cat: 'heteroaromatic', words: ['pyridine', 'thiophene', 'thiazole', 'pyrazole', 'imidazole', 'ring', 'hetero'] },
        { cat: 'toxicophore', words: ['toxic', 'toxicophore', 'alert', 'dili', 'liver', 'metabol', 'reactive', 'bioactiv'] },
        { cat: 's-interaction', words: ['sulfur', 'sigma', 'σ-hole', 'thio', 's-hole'] },
        { cat: 'interaction-tuning', words: [
            'pi-pi', 'pipi', 'pi stack', 'pi-stack', 'pi stacking', 'aromatic stack',
            'h-bond', 'hbond', 'hydrogen bond', 'h bond',
            'halogen bond', 'halogen-bond',
            'salt bridge', 'salt-bridge', 'electrostatic',
            'cation-pi', 'cation pi', 'cation-π',
            'strengthen', 'improve', 'tune', 'stronger', 'better',
            'donor', 'acceptor', 'complementarity',
        ] },
    ];

    // Score each rule
    const scored = ALL_MEDCHEM_RULES.map((rule) => {
        let score = 0;
        const fields = [
            rule.principle, rule.applicability, rule.example,
            rule.from, rule.to, rule.alert, rule.mechanism, rule.mitigation, rule.ring,
        ].filter(Boolean).join(' ').toLowerCase();

        // Direct keyword matches. Strip punctuation before tokenizing so that
        // queries like "swap-heteroaromatic-ring(compound: active, sourceRing:
        // 'phenyl', targetRing: 'pyridine')" tokenize cleanly — without this,
        // tokens like "'phenyl'," and "targetring:" don't substring-match the
        // clean rule prose ("phenyl"), causing chemistry-specific queries to
        // fall back to category-only scoring and return wrong rules first.
        // Surfaced during the 2026-05-14 Bill Kinney walkthrough prep.
        const tokens = q.replace(/[^\w\s-]/g, ' ').split(/\s+/).filter((t) => t.length > 2);
        for (const t of tokens) {
            if (fields.includes(t)) score += 2;
            if (rule.id && rule.id.includes(t)) score += 3;
        }

        // Category boost
        for (const { cat, words } of catKeywords) {
            if (rule.category === cat && words.some((w) => q.includes(w))) score += 5;
        }

        return { rule, score };
    });

    return scored
        .filter((s) => s.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, max)
        .map((s) => s.rule);
}

/**
 * Render rules for inclusion in the LLM prompt context.
 * @param {Array<object>} rules
 * @returns {string}
 */
export function formatRulesForPrompt(rules) {
    if (!rules || rules.length === 0) return '';

    const lines = ['## Medicinal-Chemistry Intuition (Meanwell)'];
    lines.push(
        'Ground your suggestions in these rules. Always cite the source when you use one.'
    );
    lines.push('');
    for (const r of rules) {
        let header;
        if (r.from && r.to) header = `**${r.from} → ${r.to}**`;
        else if (r.alert) header = `**Alert: ${r.alert}**`;
        else if (r.ring) header = `**${r.ring}**`;
        else header = `**${r.id}**`;
        lines.push(`- ${header}`);
        if (r.principle) lines.push(`  - Principle: ${r.principle}`);
        if (r.applicability) lines.push(`  - When: ${r.applicability}`);
        if (r.mechanism) lines.push(`  - Mechanism: ${r.mechanism}`);
        if (r.mitigation) lines.push(`  - Mitigation: ${r.mitigation}`);
        if (r.example) lines.push(`  - Example: ${r.example}`);
        if (r.caveats) lines.push(`  - Caveat: ${r.caveats}`);
        if (r.references && r.references.length) {
            lines.push(`  - Cite: ${r.references[0]}`);
        }
    }
    return lines.join('\n');
}

/**
 * Summary for UI surfacing (shorter than formatRulesForPrompt).
 * For toxicophore rules, appends the mitigation strategy so chat responses
 * are actionable — knowing *what* the alert is means nothing without
 * knowing *what to do about it*.
 *
 * @param {object} rule
 * @returns {string}
 */
export function formatRuleShort(rule) {
    let header;
    if (rule.from && rule.to) header = `${rule.from} → ${rule.to}`;
    else if (rule.alert) header = `Avoid: ${rule.alert}`;
    else if (rule.ring) header = rule.ring;
    else if (rule.interaction) header = `Tune ${rule.interaction}`;
    else header = rule.id;
    const body = rule.principle || rule.mechanism || '';
    const out = `**${header}** — ${body}`;
    if (rule.mitigation) {
        // No leading whitespace — markdown renderers (marked +
        // sanitize-html) treat 3-space-indented content as code-block
        // or nested-list context, which produces awkward chat
        // formatting. Plain paragraph break + italic header reads
        // cleanly when rendered by displayChatMessage in ChatBot.jsx.
        // Surfaced 2026-05-14 by Bill Kinney as Theme J (wall-of-text).
        return `${out}\n\n*Mitigation:* ${rule.mitigation}`;
    }
    return out;
}

/**
 * Filter a rule's structured `mitigationOptions` to those applicable to a
 * given compound. Each option may carry a `requiresSubstructure` array of
 * SMARTS / SMILES patterns; the option is kept only when the compound's
 * SMILES contains at least one of those patterns as a substring.
 *
 * **v1 LIMITATION — string substring matching, not real SMARTS matching.**
 * Two compounds containing the same chemical substructure can have
 * different SMILES strings (different ring-closure numbering, different
 * atom traversal order), so a literal substring match misses cases that
 * a real RDKit substructure match would catch. For the canonical case
 * Bill Kinney flagged 2026-05-14 — aniline rule offering "replace
 * piperazine/piperidine N with C" on a compound with neither — the
 * "no substring match" direction is reliable (no piperazine pattern in
 * the SMILES → safe to filter out). The "yes, substring present"
 * direction depends on the SMILES being written with matching ring
 * closure tokens, which is fragile.
 *
 * **Path to v2**: replace the substring check with an RDKit-WASM
 * substructure match using `rdkitLoader.js`. Tracked as a follow-on.
 *
 * Returns options whose `requiresSubstructure` is absent or whose at
 * least one required substructure is found in `compoundSmiles`. Options
 * without `requiresSubstructure` are always kept.
 *
 * @param {object} rule - A rule with optional `mitigationOptions`
 * @param {string} [compoundSmiles] - Active compound SMILES; if absent,
 *   returns all options (no filtering)
 * @returns {Array<object>} Applicable mitigation options
 */
export function getApplicableMitigations(rule, compoundSmiles) {
    const opts = Array.isArray(rule?.mitigationOptions) ? rule.mitigationOptions : [];
    if (opts.length === 0) return [];
    if (!compoundSmiles || typeof compoundSmiles !== 'string') return opts;
    return opts.filter((opt) => {
        const req = opt.requiresSubstructure;
        if (!Array.isArray(req) || req.length === 0) return true;
        return req.some((sub) => typeof sub === 'string' && compoundSmiles.includes(sub));
    });
}

/**
 * Render a rule's mitigations as a bulleted markdown list, filtered to
 * options applicable to the given compound. Falls back to the prose
 * `rule.mitigation` field when the rule has no structured options.
 *
 * Use this in place of `formatRuleShort` when a compound context is
 * available — the result is shorter and avoids suggestions that don't
 * apply to the user's actual molecule.
 *
 * @param {object} rule
 * @param {string} [compoundSmiles]
 * @returns {string}
 */
export function formatRuleShortForCompound(rule, compoundSmiles) {
    let header;
    if (rule.from && rule.to) header = `${rule.from} → ${rule.to}`;
    else if (rule.alert) header = `Avoid: ${rule.alert}`;
    else if (rule.ring) header = rule.ring;
    else header = rule.id;
    const body = rule.principle || rule.mechanism || '';
    const out = `**${header}** — ${body}`;

    const applicable = getApplicableMitigations(rule, compoundSmiles);
    if (applicable.length > 0) {
        // Real markdown list — no leading whitespace, blank line above
        // so the list starts a fresh markdown block. Renders as <ul> via
        // marked + sanitize-html in ChatBot.jsx::displayChatMessage.
        const bullets = applicable
            .map((opt) => `- ${opt.description}`)
            .join('\n');
        return `${out}\n\n*Mitigation:*\n${bullets}`;
    }
    if (rule.mitigation) return `${out}\n\n*Mitigation:* ${rule.mitigation}`;
    return out;
}

// Default export for convenience
export default {
    ALL_MEDCHEM_RULES,
    ISOSTERE_RULES,
    FLUORINE_RULES,
    HETEROAROMATIC_RULES,
    TOXICOPHORE_RULES,
    SULFUR_INTERACTION_RULES,
    getRulesByCategory,
    getRuleById,
    findRelevantRules,
    formatRulesForPrompt,
    formatRuleShort,
    formatRuleShortForCompound,
    getApplicableMitigations,
};
